import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { RACKSAVANT, ROOM_CAPACITY } from '@racksavant/core';
import { cx } from '../cx';
import { Wipe } from '../components/Reveal';
import { ChaosField } from '../components/ChaosField';
import { Cutout, Plate } from '../components/Photo';
import { BillingToggle, PLAN_VIEWS, PlanCard } from '../components/Plans';
import { Faq, type FaqItem } from '../components/Faq';
import { ArrowRight, Check, Dash } from '../components/icons';
import ui from '../ui.module.css';
import s from './PricingPage.module.css';

type Cell = string | true | false;

interface CompareRow {
  label: string;
  free: Cell;
  muse: Cell;
  studio: Cell;
}

interface CompareGroup {
  group: string;
  rows: CompareRow[];
}

const COMPARISON: CompareGroup[] = [
  {
    group: 'Fitting',
    rows: [
      {
        label: 'Fittings per day',
        free: `${RACKSAVANT.freeFittingsPerDay}`,
        muse: 'Unlimited',
        studio: 'Unlimited',
      },
      // These two are platform limits, not plan tiers — the API applies the same
      // ceiling to every account. The table used to quote a lower free-plan
      // number that nothing enforced.
      {
        label: 'Garments layered per look',
        free: `${RACKSAVANT.maxGarmentsPerLook}`,
        muse: `${RACKSAVANT.maxGarmentsPerLook}`,
        studio: `${RACKSAVANT.maxGarmentsPerLook}`,
      },
      {
        label: 'Body shots stored',
        free: `${RACKSAVANT.maxBodyShots}`,
        muse: `${RACKSAVANT.maxBodyShots}`,
        studio: `${RACKSAVANT.maxBodyShots}`,
      },
      { label: 'Rendering queue', free: 'Standard', muse: 'Fast', studio: 'Priority' },
      { label: 'Mood read on every look', free: true, muse: true, studio: true },
    ],
  },
  {
    group: 'The rack',
    rows: [
      { label: 'Full garment gallery', free: true, muse: true, studio: true },
      { label: 'Mood and occasion filters', free: true, muse: true, studio: true },
      { label: 'Upload your own garments', free: false, muse: false, studio: true },
      {
        label: 'Savant stylist chat',
        free: '5 messages a day',
        muse: 'Unlimited',
        studio: 'Unlimited',
      },
    ],
  },
  {
    group: 'Rooms and hype',
    rows: [
      {
        label: 'People per fitting room',
        free: `${ROOM_CAPACITY.free}`,
        muse: `${ROOM_CAPACITY.muse}`,
        studio: `${ROOM_CAPACITY.studio}`,
      },
      { label: 'Rooms you can host at once', free: '1', muse: '3', studio: '10' },
      { label: 'All seven hype reactions', free: true, muse: true, studio: true },
      { label: 'Room chat and voting', free: true, muse: true, studio: true },
    ],
  },
  {
    group: 'Publishing',
    rows: [
      { label: 'Watermark on exports', free: 'Small mark', muse: 'None', studio: 'None' },
      { label: 'Story export (9:16)', free: false, muse: true, studio: true },
      { label: 'Feed export (4:5)', free: false, muse: true, studio: true },
      { label: 'Before / after split', free: true, muse: true, studio: true },
      { label: 'Caption studio tones', free: '1', muse: 'All six', studio: 'All six' },
      { label: 'Private lookbooks', free: '1', muse: 'Unlimited', studio: 'Unlimited' },
      { label: 'Publish drops to followers', free: false, muse: false, studio: true },
    ],
  },
  {
    group: 'Work',
    rows: [
      { label: 'Per-garment analytics', free: false, muse: 'Look totals only', studio: true },
      { label: 'Commercial licence', free: false, muse: false, studio: true },
      { label: 'Support', free: 'Community', muse: 'Email', studio: 'Email, one business day' },
    ],
  },
];

const FAQS: FaqItem[] = [
  {
    q: 'What counts as one fitting?',
    a: [
      'One generated look. If you put three garments together into a single outfit, that is one fitting, not three.',
      'Rerolling the same outfit in a different mood is a new fitting, because it is a new image.',
    ],
  },
  {
    q: 'What happens when I run out on the free plan?',
    a: [
      `The counter resets at midnight in your local time and you get another ${RACKSAVANT.freeFittingsPerDay}. Nothing is deleted, nothing is locked, and we do not put a countdown on the screen.`,
      'You can still open rooms, react, browse the rack and edit your lookbooks with an empty counter.',
    ],
  },
  {
    q: 'Is yearly actually cheaper?',
    a: [
      'Yes. Yearly is twelve months for the price of ten, charged once. That is the whole discount — there is no separate annual tier with different features.',
    ],
  },
  {
    q: 'Can I switch plans in the middle of a month?',
    a: [
      'Any time. Upgrades take effect immediately and are prorated against what you have already paid. Downgrades take effect at the end of the period you have paid for, so you keep what you bought.',
    ],
  },
  {
    q: 'Do you give refunds?',
    a: [
      'If you upgrade and it is not what you expected, write to us within fourteen days and we will refund the current period in full, no interview.',
      'After that, cancelling stops the next charge rather than refunding the last one. Statutory rights in the UK and EU apply on top of this and are not affected by it.',
    ],
  },
  {
    q: 'Is there student pricing?',
    a: [
      'Yes — write to hello@racksavant.com from a student address and we will sort out Muse at half price for the academic year.',
    ],
  },
  {
    q: 'Do I pay for the people in my fitting room?',
    a: [
      'No. Rooms are free for everyone in them. Your plan sets how many people can join a room you host; nobody else is charged for being there.',
    ],
  },
  {
    q: 'What happens to my looks if I cancel?',
    a: [
      'They stay. Your looks, lookbooks and drops remain in your account on the free plan, and you can export them at any time.',
      'Features that are specific to a paid plan — watermark-free exports, drop publishing, the commercial licence — stop when the plan does.',
    ],
  },
  {
    q: 'How do I pay?',
    a: [
      'Card, Apple Pay and Google Pay, in US dollars. Prices exclude any local sales tax or VAT, which is added at checkout where it applies.',
    ],
  },
];

function CellValue({ value }: { value: Cell }) {
  if (value === true) return <Check size={16} className={s.yes} />;
  if (value === false) return <Dash size={16} className={s.no} />;
  return <>{value}</>;
}

function cellLabel(value: Cell): string | undefined {
  if (value === true) return 'Included';
  if (value === false) return 'Not included';
  return undefined;
}

export function PricingPage() {
  const [annual, setAnnual] = useState(false);

  return (
    <>
      {/* ---------------------------------------------------------- Hero */}
      <section className={s.hero} aria-labelledby="pricing-title">
        <div className={cx(ui.container, s.heroGrid)}>
          <div className={s.heroCopy}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>Pricing</p>

            <h1 id="pricing-title" className={s.heroTitle}>
              <Wipe
                lines={[
                  <span key="a" className={s.heroSmall}>
                    Free until it is
                  </span>,
                ]}
              />
              <ChaosField text="worth it" italic className={s.heroChaos} />
            </h1>

            <p className={cx(ui.lede, s.heroLede)}>
              Eight fittings a day, mood reads on all of them, and rooms with friends — no card, no
              countdown. Pay when the watermark starts annoying you.
            </p>

            <div className={s.toggleRow}>
              <BillingToggle annual={annual} onChange={setAnnual} />
            </div>
          </div>

          <div className={s.heroArt}>
            <Plate id="mirror" ratio={4 / 5} />
            <Cutout id="sunglasses" width={168} tilt={-6} className={s.heroCutout} />
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------- Cards */}
      <section className={s.cards} aria-label="Plans">
        <div className={cx(ui.container, s.cardGrid)}>
          {PLAN_VIEWS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} annual={annual} featured={plan.id === 'muse'} />
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------- Comparison */}
      <section className={cx(ui.section, s.tableSection)} aria-labelledby="compare-title">
        <div className={ui.container}>
          <div className={s.tableHead}>
            <p className={ui.eyebrow}>Everything, side by side</p>
            <h2 id="compare-title" className={ui.h2}>
              The full table.
            </h2>
          </div>

          <div className={s.tableWrap}>
            <table className={s.table}>
              <caption className={ui.srOnly}>Plan comparison</caption>
              <thead>
                <tr>
                  <th scope="col" className={s.rowLabel}>
                    Feature
                  </th>
                  {PLAN_VIEWS.map((plan) => (
                    <th key={plan.id} scope="col">
                      <span className={cx(s.colHead, plan.id === 'muse' && s.colHeadFeatured)}>
                        {plan.name}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((group) => (
                  <Fragment key={group.group}>
                    <tr className={s.groupRow}>
                      <th scope="colgroup" colSpan={4}>
                        {group.group}
                      </th>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.label}>
                        <th scope="row" className={s.rowLabel}>
                          {row.label}
                        </th>
                        <td className={s.cell} aria-label={cellLabel(row.free)}>
                          <CellValue value={row.free} />
                        </td>
                        <td className={cx(s.cell, s.cellFeatured)} aria-label={cellLabel(row.muse)}>
                          <CellValue value={row.muse} />
                        </td>
                        <td className={s.cell} aria-label={cellLabel(row.studio)}>
                          <CellValue value={row.studio} />
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className={s.tableNote}>
            Prices are in US dollars and exclude local sales tax or VAT where it applies. Yearly
            billing is twelve months for the price of ten.
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------------ FAQ */}
      <section className={ui.section} aria-labelledby="pricing-faq-title">
        <div className={ui.container}>
          <div className={s.faqHead}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>Questions about money</p>
            <h2 id="pricing-faq-title" className={ui.h2}>
              Asked and answered.
            </h2>
          </div>
          <Faq items={FAQS} />
        </div>
      </section>

      {/* ---------------------------------------------------------- Close */}
      <section className={s.close} aria-labelledby="pricing-close">
        <Plate id="duo" className={s.closePlate} caption={false} ratio={16 / 9} focus="50% 30%" />
        <div className={cx(ui.container, s.closeInner)}>
          <h2 id="pricing-close" className={s.closeTitle}>
            Start on free. Nobody will chase you.
          </h2>
          <p className={s.closeLede}>
            No card to begin, no trial timer, and delete means delete if you decide this is not for
            you.
          </p>
          <div className={s.closeCtas}>
            <Link to="/auth" className={cx(ui.btn, ui.primary)}>
              Open the studio
              <ArrowRight size={16} />
            </Link>
            <Link to="/features" className={cx(ui.btn, ui.ghost)}>
              See what you get
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
